import type { Survey } from "@/features/survey/types";
import { MarketsMenuList } from "@/pages/components/MarketsDropdown";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldMenu } from "@emerald-react/menu";
import { useState } from "react";
import { Link } from "react-router";

interface MarketSwitcherProps {
  surveys: Survey[];
  currentSurveyId: string;
  variant?: "dark" | "light";
}

/**
 * "< Market Name [CODE] ▾ >" switcher for moving between a survey group's
 * markets from within a single market's own pages; opens the same markets
 * list as the group header's dropdown.
 */
const MarketSwitcher = ({ surveys, currentSurveyId, variant = "dark" }: MarketSwitcherProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentIndex = surveys.findIndex((survey) => survey.id === currentSurveyId);
  if (currentIndex === -1 || surveys.length < 2) return null;

  const current = surveys[currentIndex];
  const prev = surveys[(currentIndex - 1 + surveys.length) % surveys.length];
  const next = surveys[(currentIndex + 1) % surveys.length];

  const isLight = variant === "light";
  const arrowClasses = isLight
    ? "flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center border border-[#dbe3e1] bg-white text-[#13211f] hover:border-[#003f2d] transition-colors"
    : "flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center border border-white/30 text-white hover:border-[#17e88f] hover:text-[#17e88f] transition-colors";
  const triggerClasses = isLight
    ? "fancy flex items-center gap-1 px-1 py-1 sm:gap-2 sm:px-2.5 sm:py-1.5 text-lg text-[#13211f] cursor-pointer"
    : "fancy flex items-center gap-1 px-1 py-1 sm:gap-2 sm:px-2.5 sm:py-1.5 text-lg text-white cursor-pointer";

  return (
    <div
      className={`flex items-center gap-0.5 mr-1 pr-2 sm:gap-1 sm:mr-3 sm:pr-4 border-r ${isLight ? "border-[#dbe3e1]" : "border-white/20"}`}
    >
      <Link
        to={`/interactive/${prev.id}/marketmap`}
        aria-label={`Previous market: ${prev.name}`}
        className={arrowClasses}
      >
        <EmeraldIcon icon="chevron_left" type="outlined" variant="symbol" />
      </Link>

      <EmeraldMenu
        position="bottom_start"
        className="markets-menu"
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        triggerElement={
          <button type="button" onClick={() => setIsOpen(!isOpen)} className={triggerClasses}>
            <span className="max-w-[70px] truncate sm:max-w-[140px]">{current.name}</span>
            <EmeraldIcon icon="arrow_drop_down" type="outlined" variant="symbol" />
          </button>
        }
      >
        <MarketsMenuList
          surveys={surveys}
          activeSurveyId={currentSurveyId}
          onNavigate={() => setIsOpen(false)}
        />
      </EmeraldMenu>

      <Link
        to={`/interactive/${next.id}/marketmap`}
        aria-label={`Next market: ${next.name}`}
        className={arrowClasses}
      >
        <EmeraldIcon icon="chevron_right" type="outlined" variant="symbol" />
      </Link>
    </div>
  );
};

export default MarketSwitcher;
